"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getBoundRefProvider = exports.createRef = exports.createMaterializer = void 0;
const inferSchema_1 = require("./inferSchema");
const utils_1 = require("./utils");
const SchemaIndex_1 = require("./SchemaIndex");
const DependencyIndex_1 = require("./DependencyIndex");
const createMaterializer = ({ transform } = {}) => {
    let template = {};
    let materialized = {};
    let schemas = {};
    let pendingInvalidations = new Set();
    let dependencyIndex = new DependencyIndex_1.DependencyIndex();
    let schemaIndex = new SchemaIndex_1.SchemaIndex(dependencyIndex);
    let firstRun = true;
    const updateTemplate = (key1, key2, key3, newTemplate) => {
        pendingInvalidations.add((0, utils_1.toStringPath)(key1, key2, key3));
        if (typeof newTemplate === 'undefined') {
            delete template[key1]?.[key2]?.[key3];
            return;
        }
        (0, utils_1.set3d)(template, key1, key2, key3, newTemplate);
    };
    const mergeSchema = (key1, key2, key3, newSchema) => {
        const baseStringPath = (0, utils_1.toStringPath)(key1, key2, key3);
        const currSchema = (0, utils_1.get3d)(schemas, key1, key2, key3);
        if (newSchema === utils_1.EMPTY_SCHEMA || (0, utils_1.isRef)(newSchema)) {
            (0, utils_1.set3d)(schemas, key1, key2, key3, newSchema);
            schemaIndex.removeSchemaFromIndex(currSchema, baseStringPath);
            if (newSchema !== utils_1.EMPTY_SCHEMA) {
                // @ts-ignore TODO Argument of type 'DataFragment' is not assignable to parameter of type 'Ref<any>'.
                dependencyIndex.addRefToIndex(newSchema, baseStringPath);
            }
            return;
        }
        if (!currSchema || currSchema === utils_1.EMPTY_SCHEMA) {
            (0, utils_1.set3d)(schemas, key1, key2, key3, newSchema);
            schemaIndex.addSchemaToIndex(newSchema, baseStringPath);
            return;
        }
        // remove all refs from currSchema
        (0, utils_1.traverse)(currSchema, (val) => dependencyIndex.removeRefFromIndex(val, baseStringPath), utils_1.SMALL_FACTOR);
        let schemaToWrite;
        (0, utils_1.traverse)(newSchema, (newRef, path) => {
            if (!(0, utils_1.isRef)(newRef)) {
                return;
            }
            schemaToWrite = schemaToWrite || currSchema || {};
            (0, utils_1.setByArray)(schemaToWrite, path, newRef);
            dependencyIndex.addRefToIndex(newRef, baseStringPath);
            return true;
        }, utils_1.SMALL_FACTOR);
        if (schemaToWrite || currSchema) {
            (0, utils_1.set3d)(schemas, key1, key2, key3, schemaToWrite || {});
        }
    };
    const populate = (invalidations, runAndReport = (fn) => fn()) => {
        const allInvalidations = runAndReport(() => (firstRun ? invalidations : dependencyIndex.getAllInvalidations(invalidations)), 'getAllInvalidations');
        const paths = runAndReport(() => dependencyIndex.toposort(allInvalidations).map(utils_1.fromStringPath), 'toposort');
        runAndReport(() => {
            for (const path of paths) {
                const [key1, key2, key3] = path;
                if (!template[key1]?.[key2]) {
                    continue;
                }
                const val = (0, utils_1.get3d)(template, key1, key2, key3);
                const nodeSchema = (0, utils_1.get3d)(schemas, key1, key2, key3);
                if (nodeSchema === utils_1.EMPTY_SCHEMA) {
                    (0, utils_1.set3d)(materialized, key1, key2, key3, transform ? transform(val, path) : val);
                    continue;
                }
                let newVal = {};
                (0, utils_1.traverse)(val, (objValue, objPath) => {
                    const schema = (0, utils_1.getByArray)(nodeSchema, objPath);
                    if (!schema) {
                        (0, utils_1.setByArray)(newVal, objPath, objValue);
                        return true;
                    }
                    if (schema.hasOwnProperty('$type')) {
                        const resolved = (0, utils_1.getByArray)(materialized, schema.refPath) ?? schema.defaultValue;
                        if (objPath.length > 0) {
                            (0, utils_1.setByArray)(newVal, objPath, resolved);
                        }
                        else {
                            newVal = resolved;
                        }
                        return true;
                    }
                    if (Array.isArray(objValue)) {
                        (0, utils_1.setByArray)(newVal, objPath, []);
                        return;
                    }
                }, utils_1.SMALL_FACTOR);
                (0, utils_1.set3d)(materialized, key1, key2, key3, transform ? transform(newVal, path) : newVal);
            }
        }, 'traverse path');
        firstRun = false;
        return paths;
    };
    const flush = (runAndReport = (fn) => fn()) => {
        const recursiveInvalidations = runAndReport(() => populate(pendingInvalidations, runAndReport), 'populate');
        runAndReport(() => pendingInvalidations.clear(), 'pendingInvalidations.clear()');
        return recursiveInvalidations;
    };
    const updateWithoutFlush = (key1, key2, key3, data, schema = (0, inferSchema_1.inferSchema)(data)) => {
        if (typeof key3 === 'undefined') {
            if (!template[key1]?.[key2]) {
                return;
            }
            for (const existingKey in template[key1][key2]) {
                mergeSchema(key1, key2, existingKey, utils_1.EMPTY_SCHEMA);
                updateTemplate(key1, key2, existingKey, undefined);
            }
            delete schemas[key1]?.[key2];
            delete template[key1]?.[key2];
            delete materialized[key1]?.[key2];
            return;
        }
        mergeSchema(key1, key2, key3, schema ?? utils_1.EMPTY_SCHEMA);
        updateTemplate(key1, key2, key3, data);
    };
    function clean3dObject(obj) {
        for (const key1 in obj) {
            for (const key2 in obj[key1]) {
                for (const key3 in obj[key1][key2]) {
                    delete obj[key1][key2][key3];
                }
                delete obj[key1][key2];
            }
            delete obj[key1];
        }
    }
    return {
        cleanAll() {
            clean3dObject(template);
            template = {};
            clean3dObject(materialized);
            materialized = {};
            clean3dObject(schemas);
            schemas = {};
            if (pendingInvalidations) {
                pendingInvalidations.clear();
            }
            pendingInvalidations = new Set();
            if (dependencyIndex) {
                dependencyIndex.clean();
            }
            dependencyIndex = new DependencyIndex_1.DependencyIndex();
            schemaIndex = new SchemaIndex_1.SchemaIndex(dependencyIndex);
        },
        update(key1, key2, key3, data, schema) {
            updateWithoutFlush(key1, key2, key3, data, schema);
            return flush();
        },
        batch(batchFunction, runAndReport = (fn) => fn()) {
            batchFunction(updateWithoutFlush);
            return runAndReport(() => flush(runAndReport), 'flush');
        },
        get: (path) => (0, utils_1.getByArray)(materialized, Array.isArray(path) ? path : path.split('.')),
    };
};
exports.createMaterializer = createMaterializer;
const createRef = (refPath, defaultValue) => {
    if (refPath.length < utils_1.DEPTH) {
        throw new Error(`Unsupported ref path, must be ${utils_1.DEPTH} or more levels deep`);
    }
    return { $type: 'ref', refPath, defaultValue };
};
exports.createRef = createRef;
function getBoundRefProvider(pathToBind) {
    return (innerPath, defaultValue) => (0, exports.createRef)([...pathToBind, ...innerPath], defaultValue);
}
exports.getBoundRefProvider = getBoundRefProvider;
//# sourceMappingURL=materializer.js.map